import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw, User, Fingerprint, Map, ChevronDown, ChevronUp, GraduationCap, Globe, BookOpen, Users, Download, Share2, Check, Brain, Flame, Compass, Bot, Clock, DollarSign, TrendingUp as TrendingIcon, Briefcase, Zap, MessageSquare, CalendarDays, Star, AlertCircle, CheckCircle2, MessageCircleHeart, X } from 'lucide-react';
import { AssessmentResults, VersionConfig } from '@/types/assessment';
import { exportToPDF, exportActivitiesList, exportCounselorSummary, getShareUrl } from '@/lib/results';
import { findMatchingQuote } from '@/lib/quotes';
import { getEduIcon } from '@/lib/eduIcons';
import { getClusterIcon } from '@/lib/clusterIcons';
import SilhouetteChart from './SilhouetteChart';
import SmartRecommendations from './SmartRecommendations';
import PathComparison from './PathComparison';
import TerrainBackground from './TerrainBackground';
import RiasecProfile from './RiasecProfile';
import { scoreRiasec } from '@/lib/riasec';
import { demandBand, resilienceBand } from '@/lib/outlook';

const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const staggerCard = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  show: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring' as const, stiffness: 200, damping: 22 } },
};
interface ResultsViewProps {
  results: AssessmentResults;
  config: VersionConfig;
  onStartOver: () => void;
  shareId?: string | null;
  selections?: Record<string, string[]>;
}

const tabs = [
  { id: 'you', label: 'You', icon: User },
  { id: 'strengths', label: 'Signature Strengths', icon: Fingerprint },
  { id: 'paths', label: 'Career Paths', icon: Map },
];

const BETA_FEEDBACK_URL = import.meta.env.VITE_BETA_FEEDBACK_URL as string | undefined;
const BETA_FEEDBACK_DISMISS_KEY = 'tp_beta_feedback_dismissed';

const ResultsView = ({ results, config, onStartOver, shareId, selections }: ResultsViewProps) => {
  const [activeTab, setActiveTab] = useState('you');
  const [activePath, setActivePath] = useState(0);
  const [shortlisted, setShortlisted] = useState<number[]>([]);
  const [showComparison, setShowComparison] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);
  const [feedbackDismissed, setFeedbackDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(BETA_FEEDBACK_DISMISS_KEY) === '1';
  });

  const dismissFeedback = () => {
    setFeedbackDismissed(true);
    try { window.localStorage.setItem(BETA_FEEDBACK_DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  const showFeedbackBanner = !!BETA_FEEDBACK_URL && !feedbackDismissed;

  const handleTabKeyDown = (e: React.KeyboardEvent) => {
    const idx = tabs.findIndex(t => t.id === activeTab);
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setActiveTab(tabs[(idx + 1) % tabs.length].id);
      setShowComparison(false);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setActiveTab(tabs[(idx - 1 + tabs.length) % tabs.length].id);
      setShowComparison(false);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveTab(tabs[0].id);
      setShowComparison(false);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActiveTab(tabs[tabs.length - 1].id);
      setShowComparison(false);
    }
  };

  const toggleShortlist = (idx: number) => {
    setShortlisted(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const [copiedParent, setCopiedParent] = useState(false);

  const handleCopyLink = () => {
    if (!shareId) return;
    navigator.clipboard.writeText(getShareUrl(shareId));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareWithParent = () => {
    if (!shareId) return;
    navigator.clipboard.writeText(`${getShareUrl(shareId)}?view=parent`);
    setCopiedParent(true);
    setTimeout(() => setCopiedParent(false), 2000);
  };

  return (
    <div className="min-h-screen bg-journey-atmosphere relative overflow-hidden">
      <TerrainBackground count={4} />
      {/* Header reveal line */}
      <motion.div
        className="absolute top-0 left-0 h-0.5 bg-gradient-to-r from-primary via-accent to-primary"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
        style={{ transformOrigin: 'left' }}
        aria-hidden="true"
      />

      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between border-b border-border/30">
        <div className="flex items-center gap-3">
          <span className="text-primary font-display text-lg font-bold">TruePath</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30" aria-label="Blueprint status: Ready">
            Your True Path
          </span>
        </div>
        <div className="flex items-center gap-2">
          {shareId && (
            <button
              onClick={handleCopyLink}
              aria-label={copied ? 'Link copied to clipboard' : 'Copy share link'}
              aria-live="polite"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-card border border-border text-muted-foreground hover:text-foreground transition-all"
            >
              {copied ? <Check className="w-3 h-3 text-primary" aria-hidden="true" /> : <Share2 className="w-3 h-3" aria-hidden="true" />}
              {copied ? 'Copied!' : 'Share Link'}
            </button>
          )}
          {/* Activities List and Counselor Summary are in admin/counselor view only */}
          <button
            onClick={() => exportToPDF(results, config.label, config.edition)}
            aria-label="Export as PDF"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-card border border-border text-muted-foreground hover:text-foreground transition-all"
          >
            <Download className="w-3 h-3" aria-hidden="true" /> Export PDF
          </button>
          <button
            onClick={onStartOver}
            aria-label="Start assessment over"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <RotateCcw className="w-3 h-3" aria-hidden="true" /> Start Over
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="px-6 py-3 border-b border-border/30 overflow-x-auto bg-card/60 backdrop-blur-sm sticky top-0 z-10">
        <div
          className="flex gap-1.5 min-w-max"
          role="tablist"
          aria-label="Results sections"
          onKeyDown={handleTabKeyDown}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={activeTab === tab.id}
              aria-controls={`panel-${tab.id}`}
              tabIndex={activeTab === tab.id ? 0 : -1}
              onClick={() => { setActiveTab(tab.id); setShowComparison(false); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary border border-border/50'
              }`}
            >
              <tab.icon className="w-4 h-4" aria-hidden="true" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Fallback notice */}
      {results._fallback && (
        <div className="px-6 py-2 bg-secondary border-b border-border/30" role="alert">
          <p className="text-xs text-muted-foreground text-center">
            Using a standard path — AI personalization was unavailable.{' '}
            <span className="text-primary">Sign in and retake for your personalized version.</span>
          </p>
        </div>
      )}

      {/* Beta feedback banner */}
      {showFeedbackBanner && (
        <div className="px-4 pt-4 relative z-10">
          <div className="max-w-3xl mx-auto rounded-2xl border border-primary/30 bg-primary/8 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
              <MessageCircleHeart className="w-4 h-4 text-primary" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">Help shape what comes next.</p>
              <p className="text-xs text-muted-foreground">
                3-minute anonymous feedback. Brutal honesty is the goal.
              </p>
            </div>
            <a
              href={BETA_FEEDBACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shrink-0"
            >
              Share feedback
            </a>
            <button
              onClick={dismissFeedback}
              aria-label="Dismiss feedback banner"
              className="p-1 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-background/50 transition-colors shrink-0"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <main id="main-content" className="max-w-3xl mx-auto px-4 py-8 relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab + (showComparison ? '-compare' : '')}
            role="tabpanel"
            id={`panel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
            tabIndex={0}
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ type: 'spring' as const, stiffness: 250, damping: 25 }}
          >
            {activeTab === 'you' && (
              <YouTab results={results} selections={selections} />
            )}

            {activeTab === 'strengths' && (
              <StrengthsTab results={results} />
            )}

            {activeTab === 'paths' && !showComparison && (
              <PathsTab
                results={results}
                config={config}
                activePath={activePath}
                setActivePath={setActivePath}
                shortlisted={shortlisted}
                toggleShortlist={toggleShortlist}
                setShowComparison={setShowComparison}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                selections={selections}
              />
            )}

            {activeTab === 'paths' && showComparison && (
              <PathComparison
                paths={results.paths}
                matches={results.pathMatches}
                shortlisted={shortlisted}
                config={config}
                onBack={() => setShowComparison(false)}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Methodology footer: shown on every results tab so the academic
            grounding is always one scroll away, not buried on a separate page. */}
        <footer className="mt-16 pt-8 border-t border-border/30">
          <div className="space-y-3">
            <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground/70 text-center">
              How this was built
            </p>
            <p className="text-xs text-muted-foreground/80 leading-relaxed text-center max-w-2xl mx-auto">
              Your answers were mapped to <strong className="text-foreground/90">Holland&apos;s RIASEC interest framework</strong> (1959, the most-validated career model in vocational psychology). Paths were drawn from the <strong className="text-foreground/90">U.S. Department of Labor&apos;s O*NET database</strong> (900+ occupations), with growth and wage data from the <strong className="text-foreground/90">Bureau of Labor Statistics</strong> 2024-2034 projections. AI resilience scores reflect the <strong className="text-foreground/90">WEF Future of Jobs Report</strong> and <strong className="text-foreground/90">McKinsey Global Institute</strong> automation modeling.
            </p>
            <p className="text-[10px] text-muted-foreground/50 text-center pt-2">
              Read the full <a href={`${window.location.pathname.split('/').slice(0, 2).join('/')}/about`} className="underline hover:text-foreground transition-colors">methodology</a>.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
};

/* -- You Tab -- */
const youSections = [
  { key: 'brainWiring', title: 'How your brain works', icon: Brain },
  { key: 'signatureStrengths', title: 'Your signature strengths', icon: Fingerprint },
  { key: 'interests', title: 'What lights you up', icon: Flame },
  { key: 'direction', title: 'Where you\'re headed', icon: Compass },
  { key: 'aiRealityCheck', title: 'AI reality check', icon: Bot },
] as const;

const YouTab = ({ results, selections }: { results: AssessmentResults; selections?: Record<string, string[]> }) => {
  // Identify the strongest path match for the celebration card.
  const topMatch = results.pathMatches?.length
    ? [...results.pathMatches].sort((a, b) => b.matchPercent - a.matchPercent)[0]
    : null;
  const topPath = topMatch ? results.paths[topMatch.pathIndex] : null;
  const topReason = topMatch?.reasons?.[0];

  // Compute the user's RIASEC interest profile from their selections.
  // Done client-side so it renders instantly without waiting for the AI step.
  const riasecScores = selections ? scoreRiasec(selections) : null;

  return (
  <motion.div className="space-y-6" variants={staggerContainer} initial="hidden" animate="show">
    <h2 className="text-3xl font-display font-bold text-foreground">Your Profile</h2>

    {/* Top Match celebration — bridges the confetti reveal to the detail content */}
    {topPath && topMatch && (
      <motion.div
        variants={staggerCard}
        className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/12 via-primary/6 to-accent/10 p-5 md:p-6"
      >
        {/* Soft animated radial highlight */}
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute -top-12 -right-12 w-56 h-56 rounded-full"
          style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.20), transparent 70%)' }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
        <div className="relative flex items-start gap-4">
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 16, delay: 0.15 }}
            className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center shrink-0 glow-teal"
          >
            <Star className="w-6 h-6 text-primary" aria-hidden="true" />
          </motion.div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-primary/80">
              Your strongest fit
            </p>
            <h3 className="font-display font-bold text-2xl md:text-3xl text-foreground leading-tight mt-0.5">
              {topPath.name}
            </h3>
            {topReason && (
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                {topReason}
              </p>
            )}
          </div>
          <div className="text-right shrink-0 ml-2">
            <motion.p
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.4 }}
              className="font-display font-bold text-3xl md:text-4xl bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent leading-none"
            >
              {topMatch.matchPercent}%
            </motion.p>
            <p className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wider">
              match
            </p>
          </div>
        </div>
      </motion.div>
    )}

    {/* RIASEC interest profile — anchored to Holland's framework. Shows
        the user's top 3 interest codes in plain language with a bar viz
        of all 6. Builds trust by exposing the methodology, not just the
        AI's narrative. */}
    {riasecScores && (
      <motion.div variants={staggerCard}>
        <RiasecProfile scores={riasecScores} variant="full" />
      </motion.div>
    )}

    <SilhouetteChart data={results.radarData} />

    {results.youSummary.cognitiveProfile && (
      <motion.div variants={staggerCard} className="glass-card p-5 space-y-3 border-primary/30">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Brain className="w-4 h-4 text-primary" aria-hidden="true" />
          Cognitive Style: {results.youSummary.cognitiveProfile.style}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{results.youSummary.cognitiveProfile.description}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <p className="text-xs font-medium text-primary">Strengths</p>
            {results.youSummary.cognitiveProfile.strengths.map(s => (
              <p key={s} className="text-xs text-muted-foreground flex items-center gap-1">
                <span className="text-primary">▸</span> {s}
              </p>
            ))}
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-accent">Blind Spots</p>
            {results.youSummary.cognitiveProfile.blindSpots.map(s => (
              <p key={s} className="text-xs text-muted-foreground flex items-center gap-1">
                <span className="text-accent">▸</span> {s}
              </p>
            ))}
          </div>
        </div>
      </motion.div>
    )}

    {/* Motivational quote matched to student's assessment */}
    {selections && (
      <motion.div
        variants={staggerCard}
        className="glass-card p-6 text-center space-y-2 border-primary/20 bg-primary/5"
      >
        <p className="text-base italic text-foreground leading-relaxed">
          "{findMatchingQuote(selections).text}"
        </p>
        <p className="text-xs text-primary font-medium">
          — {findMatchingQuote(selections).author}
        </p>
      </motion.div>
    )}

    <motion.div className="space-y-4" variants={staggerContainer} initial="hidden" animate="show">
      {youSections.map((section) => (
        <motion.div key={section.key} variants={staggerCard} className="glass-card p-5 space-y-2">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <section.icon className="w-4 h-4 text-primary" aria-hidden="true" />
            {section.title}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {results.youSummary[section.key]}
          </p>
        </motion.div>
      ))}
    </motion.div>
  </motion.div>
  );
};

/* -- Signature Strengths Tab -- */
const StrengthsTab = ({ results }: { results: AssessmentResults }) => {
  // Connect strengths to the user's actual top-matched path using real
  // path data (name + trending roles). No fabricated per-strength claims.
  const topMatch = results.pathMatches?.length
    ? [...results.pathMatches].sort((a, b) => b.matchPercent - a.matchPercent)[0]
    : null;
  const topPath = topMatch ? results.paths[topMatch.pathIndex] : null;
  const topRoles = topPath?.globalOutlook?.trendingRoles?.slice(0, 3) || [];

  return (
  <motion.div className="space-y-6" variants={staggerContainer} initial="hidden" animate="show">
    <h2 className="text-3xl font-display font-bold text-foreground">Your Signature Strengths</h2>

    {topPath && (
      <motion.div variants={staggerCard} className="glass-card p-5 space-y-2 border-primary/25">
        <p className="text-[10px] font-semibold tracking-[0.16em] uppercase text-primary/80">
          Where these strengths take you
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          These are the strengths your answers surfaced. In your strongest match,{' '}
          <span className="text-foreground font-semibold">{topPath.name}</span>, they show up directly in the day-to-day work.
          {topRoles.length > 0 && (
            <>
              {' '}Related roles people move into: <span className="text-foreground">{topRoles.join(', ')}</span>.
            </>
          )}
        </p>
      </motion.div>
    )}

    <div className="grid gap-4">
      {results.strengthClusters.map((cluster, idx) => {
        const ClusterIcon = getClusterIcon(cluster.emoji);
        return (
          <motion.div
            key={cluster.name}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ type: 'spring', stiffness: 200, damping: 22, delay: idx * 0.06 }}
            className={`glass-card p-5 space-y-2 ${cluster.color === 'teal' ? 'border-primary/30' : 'border-accent/30'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cluster.color === 'teal' ? 'bg-primary/15' : 'bg-accent/15'}`}>
                <ClusterIcon className={`w-5 h-5 ${cluster.color === 'teal' ? 'text-primary' : 'text-accent'}`} aria-hidden="true" />
              </div>
              <h3 className={`font-display font-bold text-lg ${cluster.color === 'teal' ? 'text-primary' : 'text-accent'}`}>
                {cluster.name}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">{cluster.description}</p>
            <p className="text-xs text-primary italic">{cluster.stat}</p>
          </motion.div>
        );
      })}
    </div>
  </motion.div>
  );
};

/* -- Paths Tab -- */
interface PathsTabProps {
  results: AssessmentResults;
  config: VersionConfig;
  activePath: number;
  setActivePath: (i: number) => void;
  shortlisted: number[];
  toggleShortlist: (i: number) => void;
  setShowComparison: (v: boolean) => void;
  expandedSections: Record<string, boolean>;
  toggleSection: (key: string) => void;
  selections?: Record<string, string[]>;
}

const PathsTab = ({
  results, config, activePath, setActivePath,
  shortlisted, toggleShortlist, setShowComparison,
  expandedSections, toggleSection, selections,
}: PathsTabProps) => {
  const path = results.paths[activePath];

  return (
    <div className="space-y-8">
      <h2 className="text-3xl font-display font-bold text-foreground">Your Career Paths</h2>

      <SmartRecommendations
        paths={results.paths}
        matches={results.pathMatches}
        config={config}
        shortlisted={shortlisted}
        onToggleShortlist={toggleShortlist}
        onViewComparison={() => setShowComparison(true)}
        selections={selections}
      />

      {/* Detailed Roadmap */}
      <div className="space-y-4 pt-4 border-t border-border/30">
        <h3 className="font-display font-semibold text-foreground text-lg">Detailed Roadmap</h3>
        <div className="relative flex gap-2 p-1 rounded-2xl bg-card/60 border border-border/40">
          {results.paths.map((p, i) => {
            const pathName = p.name || `Path ${i + 1}`;
            const isActive = activePath === i;
            return (
              <motion.button
                key={i}
                onClick={() => setActivePath(i)}
                aria-pressed={isActive}
                whileHover={{ y: isActive ? 0 : -1 }}
                whileTap={{ scale: 0.98 }}
                className={`relative flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {/* Sliding active indicator (shared layoutId animates between buttons) */}
                {isActive && (
                  <motion.span
                    layoutId="path-tab-indicator"
                    className="absolute inset-0 rounded-xl bg-primary/12 border border-primary/40 shadow-md shadow-primary/10"
                    transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                    aria-hidden="true"
                  />
                )}
                <span className="relative">{pathName}</span>
              </motion.button>
            );
          })}
        </div>

        {path && (
          <motion.div key={activePath} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-muted-foreground">{path.description}</p>

            {/* Educational Paths */}
            {path.educationalPaths && path.educationalPaths.length > 0 && (
              <CollapsibleSection
                title="Educational Paths"
                icon={<GraduationCap className="w-4 h-4 text-primary" aria-hidden="true" />}
                sectionKey={`edu-${activePath}`}
                expanded={expandedSections[`edu-${activePath}`] !== false}
                onToggle={toggleSection}
              >
                <div className="space-y-3">
                  {path.educationalPaths.map((ep) => {
                    const EduIcon = getEduIcon(ep.type);
                    return (
                      <div key={ep.name} className="glass-card p-4 space-y-2">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                            <EduIcon className="w-4 h-4 text-primary" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground text-sm">{ep.name}</h4>
                            <p className="text-xs text-muted-foreground mt-1">{ep.description}</p>
                            <div className="flex flex-wrap gap-3 mt-2">
                              <span className="text-xs text-primary flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {ep.duration}
                              </span>
                              <span className="text-xs text-accent flex items-center gap-1">
                                <DollarSign className="w-3 h-3" /> {ep.cost}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground italic mt-1">Best for: {ep.bestFor}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CollapsibleSection>
            )}

            {/* Timeline Roadmap */}
            {path.actions && config.timelines.map((timeline) => {
              const actions = path.actions?.[timeline];
              const skills = path.skills?.[timeline];
              if (!actions) return null;
              return (
                <div key={timeline} className="glass-card p-5 space-y-3">
                  <h3 className="font-display font-semibold text-primary">{timeline}</h3>
                  <div className="space-y-2">
                    {actions.map((action, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-primary mt-0.5 shrink-0">▸</span>
                        <span className="text-foreground/90">{action}</span>
                      </div>
                    ))}
                  </div>
                  {skills && (
                    <div className="pt-2 border-t border-border/50">
                      <p className="text-xs text-muted-foreground mb-1">Skills you'll build:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {skills.map((skill) => (
                          <span key={skill} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Skills to Stack */}
            {path.skillsToStack && (
              <CollapsibleSection
                title="Skills to Stack"
                icon={<BookOpen className="w-4 h-4 text-primary" aria-hidden="true" />}
                sectionKey={`skills-${activePath}`}
                expanded={expandedSections[`skills-${activePath}`] !== false}
                onToggle={toggleSection}
              >
                <div className="space-y-4">
                  {path.skillsToStack.courses?.length > 0 && (
                    <div className="glass-card p-4 space-y-3">
                      <h4 className="text-sm font-semibold text-primary">Courses & Learning</h4>
                      {path.skillsToStack.courses.map((course, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className="text-primary mt-0.5 shrink-0">▸</span>
                          <span className="text-foreground/90">{course}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {path.skillsToStack.portfolio?.length > 0 && (
                    <div className="glass-card p-4 space-y-3">
                      <h4 className="text-sm font-semibold text-accent">Portfolio Items</h4>
                      {path.skillsToStack.portfolio.map((item, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className="text-accent mt-0.5 shrink-0">▸</span>
                          <span className="text-foreground/90">{item}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {path.skillsToStack.connections?.length > 0 && (
                    <div className="glass-card p-4 space-y-3">
                      <h4 className="text-sm font-semibold text-foreground flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" /> People to Connect With
                      </h4>
                      {path.skillsToStack.connections.map((conn, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className="text-primary mt-0.5 shrink-0">▸</span>
                          <span className="text-foreground/90">{conn}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CollapsibleSection>
            )}

            {/* Global Outlook */}
            <CollapsibleSection
              title="Global Outlook"
              icon={<Globe className="w-4 h-4 text-primary" aria-hidden="true" />}
              sectionKey={`global-${activePath}`}
              expanded={expandedSections[`global-${activePath}`] !== false}
              onToggle={toggleSection}
            >
              <div className="glass-card p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-foreground">Top Region: {path.globalOutlook.topRegion}</h4>
                  <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                    {path.globalOutlook.growthRate}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 rounded-xl bg-primary/10">
                    <p className="text-base font-display font-bold text-primary">{demandBand(path.globalOutlook.demandScore)}</p>
                    <p className="text-[10px] text-muted-foreground">Demand (est.)</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-secondary">
                    <p className="text-sm font-bold text-foreground">{path.globalOutlook.avgSalary}</p>
                    <p className="text-[10px] text-muted-foreground">Avg Salary</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-accent/10">
                    <p className="text-base font-display font-bold text-accent">{resilienceBand(path.globalOutlook.aiResilienceScore)}</p>
                    <p className="text-[10px] text-muted-foreground">AI Resilience (est.)</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-border/50">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Trending Roles</p>
                  <div className="flex flex-wrap gap-1.5">
                    {path.globalOutlook.trendingRoles.map(role => (
                      <span key={role} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">{role}</span>
                    ))}
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Monetize Now / Projects to Start */}
            {path.monetizeNow && path.monetizeNow.length > 0 && (
              <div className="glass-card p-5 space-y-3 border-accent/30">
                <h3 className="font-display font-semibold text-accent flex items-center gap-2">
                  {config.edition === 'highschool' ? (
                    <><Zap className="w-4 h-4" aria-hidden="true" /> Projects to Start</>
                  ) : (
                    <><DollarSign className="w-4 h-4" aria-hidden="true" /> Monetize Now</>
                  )}
                </h3>
                {path.monetizeNow.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-accent mt-0.5 shrink-0">▸</span>
                    <span className="text-foreground/90">{item}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Gig Work & Side Hustles */}
            {path.gigAndSideHustles && (
              <CollapsibleSection
                title="Gig Work & Side Hustles"
                icon={<Briefcase className="w-4 h-4 text-accent" aria-hidden="true" />}
                sectionKey={`gig-${activePath}`}
                expanded={expandedSections[`gig-${activePath}`] !== false}
                onToggle={toggleSection}
              >
                <div className="space-y-4">
                  {/* Gig ideas */}
                  <div className="space-y-3">
                    {path.gigAndSideHustles.ideas.map((gig, i) => (
                      <div key={i} className="glass-card p-4 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground text-sm">{gig.title}</h4>
                            <p className="text-xs text-muted-foreground mt-1">{gig.description}</p>
                          </div>
                          <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            gig.difficulty === 'beginner' ? 'bg-accent/15 text-accent' :
                            gig.difficulty === 'intermediate' ? 'bg-primary/15 text-primary' :
                            'bg-foreground/10 text-foreground/70'
                          }`}>
                            {gig.difficulty}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <DollarSign className="w-3 h-3 text-accent" />
                          <span className="text-xs font-medium text-accent">{gig.earning}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Platforms */}
                  <div className="glass-card p-4 space-y-2">
                    <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Zap className="w-3 h-3 text-primary" /> Platforms to Get Started
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {path.gigAndSideHustles.platforms.map(platform => (
                        <span key={platform} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">{platform}</span>
                      ))}
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="glass-card p-4 space-y-2">
                    <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">Pro Tips</h4>
                    {path.gigAndSideHustles.tips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-primary mt-0.5 shrink-0">▸</span>
                        <span className="text-foreground/90">{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CollapsibleSection>
            )}

            {/* Counselor/Advisor Toolkit — HS and College only */}
            {path.counselorToolkit && config.edition !== 'professional' && (
              <CollapsibleSection
                title={config.edition === 'college' ? 'Advisor Toolkit' : 'Counselor Toolkit'}
                icon={<MessageSquare className="w-4 h-4 text-primary" aria-hidden="true" />}
                sectionKey={`counselor-${activePath}`}
                expanded={expandedSections[`counselor-${activePath}`] !== false}
                onToggle={toggleSection}
              >
                <div className="space-y-4">
                  <div className="glass-card p-4 border-primary/20 bg-primary/5">
                    <p className="text-sm text-foreground/90">
                      <strong>{config.edition === 'college' ? 'Bring this to your academic advisor.' : 'Bring this to your guidance counselor.'}</strong> These course and club recommendations are tailored to your career path. Use the talking points below to guide your conversation.
                    </p>
                  </div>

                  {/* Recommended Courses */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-primary" /> Recommended Courses
                    </h4>
                    <div className="space-y-2">
                      {path.counselorToolkit.recommendedCourses.map((course, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }} className="glass-card p-3 space-y-1.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 flex-1">
                              <div className={`mt-0.5 shrink-0 ${course.priority === 'must-take' ? 'text-primary' : course.priority === 'recommended' ? 'text-accent' : 'text-muted-foreground'}`}>
                                {course.priority === 'must-take' ? <Star className="w-3.5 h-3.5" /> : course.priority === 'recommended' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                              </div>
                              <div className="flex-1">
                                <h5 className="text-sm font-semibold text-foreground">{course.name}</h5>
                                <p className="text-xs text-muted-foreground mt-0.5">{course.why}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${course.category === 'ap-honors' ? 'bg-primary/15 text-primary' : course.category === 'cte' ? 'bg-accent/15 text-accent' : course.category === 'elective' ? 'bg-secondary text-secondary-foreground' : 'bg-muted text-muted-foreground'}`}>
                                {course.category === 'ap-honors' ? 'AP/Honors' : course.category === 'cte' ? 'CTE' : course.category === 'elective' ? 'Elective' : 'Core'}
                              </span>
                              {course.semester && <span className="text-[10px] text-muted-foreground">{course.semester}</span>}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 text-[10px] text-muted-foreground pt-1">
                      <span className="flex items-center gap-1"><Star className="w-3 h-3 text-primary" /> Must-take</span>
                      <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-accent" /> Recommended</span>
                      <span className="flex items-center gap-1"><AlertCircle className="w-3 h-3 text-muted-foreground" /> Nice to have</span>
                    </div>
                  </div>

                  {/* Clubs & Organizations */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Users className="w-4 h-4 text-accent" /> Clubs & Organizations to Join
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {path.counselorToolkit.clubsAndOrgs.map((club, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="glass-card p-3 space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <h5 className="text-xs font-semibold text-foreground">{club.name}</h5>
                            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-medium ${club.type === 'competition' ? 'bg-primary/15 text-primary' : club.type === 'leadership' ? 'bg-accent/15 text-accent' : club.type === 'creative' ? 'bg-foreground/10 text-foreground/70' : 'bg-secondary text-secondary-foreground'}`}>
                              {club.type}
                            </span>
                          </div>
                          <p className="text-[11px] text-muted-foreground">{club.why}</p>
                          <div className="flex items-center gap-1 pt-0.5">
                            <Clock className="w-2.5 h-2.5 text-muted-foreground" />
                            <span className={`text-[10px] font-medium ${club.commitment === 'high' ? 'text-primary' : club.commitment === 'medium' ? 'text-accent' : 'text-muted-foreground'}`}>
                              {club.commitment} commitment
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Sample Schedule */}
                  {path.counselorToolkit.sampleSchedule && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-primary" /> Sample Course Schedule
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(path.counselorToolkit.sampleSchedule).map(([semester, courses], i) => (
                          <motion.div key={semester} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }} className="glass-card p-3">
                            <h5 className="text-xs font-semibold text-primary mb-1.5">{semester}</h5>
                            <div className="flex flex-wrap gap-1.5">
                              {courses.map(c => (
                                <span key={c} className="px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground text-[11px]">{c}</span>
                              ))}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Talking Points */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-accent" /> Talking Points for Your Counselor
                    </h4>
                    <div className="glass-card p-4 space-y-2 border-accent/20">
                      {path.counselorToolkit.talkingPoints.map((point, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className="text-accent mt-0.5 shrink-0 font-bold text-xs">{i + 1}.</span>
                          <span className="text-foreground/90 italic">"{point}"</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CollapsibleSection>
            )}

            {path.futureProofing && path.futureProofing.length > 0 && (
              <div className="glass-card p-5 space-y-3 border-primary/30">
                <h3 className="font-display font-semibold text-primary flex items-center gap-2">
                  <TrendingIcon className="w-4 h-4" aria-hidden="true" /> What's Shifting
                </h3>
                {path.futureProofing.map((tip, i) => (
                  <p key={i} className="text-sm text-muted-foreground italic">{tip}</p>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

/* -- Collapsible Section -- */
const CollapsibleSection = ({
  title, icon, sectionKey, expanded, onToggle, children,
}: {
  title: string; icon: React.ReactNode; sectionKey: string;
  expanded: boolean; onToggle: (key: string) => void; children: React.ReactNode;
}) => (
  <div className="space-y-3">
    <motion.button
      onClick={() => onToggle(sectionKey)}
      aria-expanded={expanded}
      aria-controls={`section-${sectionKey}`}
      className="flex items-center justify-between w-full text-left"
      whileHover={{ x: 2 }}
      whileTap={{ scale: 0.99 }}
    >
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="font-display font-semibold text-foreground">{title}</h3>
      </div>
      <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }} aria-hidden="true">
        <ChevronDown className="w-4 h-4 text-muted-foreground" />
      </motion.div>
    </motion.button>
    <AnimatePresence>
      {expanded && (
        <motion.div
          id={`section-${sectionKey}`}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default ResultsView;
